import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { checkAll, checkWorkspaceRole, IPermissionCheckResult } from '../../../modules/permissions/permissionsHelpers';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { IUserStore, Plan } from '../user/user.store';
import { WorkspaceRole } from '../workspaces/workspaces.store';
import { DatasetStatus } from './datasetStatus.model';

interface IDatasetsPermissions {
  canRenameImages(datasetStatus: DatasetStatus, datasetCreator: string): IPermissionCheckResult;
  canDeleteUnmarkedImages(datasetStatus: DatasetStatus, datasetCreator: string): IPermissionCheckResult;
  canDeleteMarkedImages(): IPermissionCheckResult;
  canDownloadImages(datasetStatus: DatasetStatus, datasetCreatorId: string): IPermissionCheckResult;
  canCreateDataset(): IPermissionCheckResult;
  canUploadImages(datasetStatus: DatasetStatus, datasetCreatorId: string): IPermissionCheckResult;
  canEditDatasetDetails(datasetStatus: DatasetStatus, datasetCreatorId: string): IPermissionCheckResult;
  canSeeDatasetCreatedBy(): IPermissionCheckResult;
  canPublishDataset(): IPermissionCheckResult;
  canDownloadDataset(): IPermissionCheckResult;
  canDeleteDataset(datasetStatus: DatasetStatus, datasetCreatorId: string): IPermissionCheckResult;
  canViewDatasets(): IPermissionCheckResult;
}

export const useDatasetPermissions = (): IDatasetsPermissions => {
  const { currentWorkspace } = useInjection(ICurrentWorkspaceStore.$);
  const { userInfo } = useInjection(IUserStore.$);
  const { t } = useTranslator();

  const canViewDatasets = (): IPermissionCheckResult => {
    return checkWorkspaceRole(
      [
        WorkspaceRole.Owner,
        WorkspaceRole.Manager,
        WorkspaceRole.Developer,
        WorkspaceRole.Trainer,
        WorkspaceRole.Assistant,
        WorkspaceRole.Collaborator,
        WorkspaceRole.Guest,
      ],
      currentWorkspace,
      t
    );
  };

  const canRenameImages = (datasetStatus: DatasetStatus, datasetCreatorId: string): IPermissionCheckResult => {
    const permittedRolesScope =
      datasetStatus === 'Published'
        ? [WorkspaceRole.Owner, WorkspaceRole.Manager]
        : [WorkspaceRole.Developer, WorkspaceRole.Trainer, WorkspaceRole.Collaborator, WorkspaceRole.Manager, WorkspaceRole.Owner];

    if (datasetStatus !== 'Published' && datasetStatus !== 'PublishedUploading' && userInfo.id === datasetCreatorId) {
      permittedRolesScope.push(WorkspaceRole.Assistant);
    }

    return checkAll([Plan.Admin, Plan.Annotation, Plan.Training], permittedRolesScope, currentWorkspace, t);
  };

  const canDeleteUnmarkedImages = (datasetStatus: DatasetStatus, datasetCreatorId: string): IPermissionCheckResult => {
    const permittedRolesScope = [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer, WorkspaceRole.Collaborator];

    if (datasetStatus !== 'Published' && datasetStatus !== 'PublishedUploading' && userInfo.id === datasetCreatorId) {
      permittedRolesScope.push(WorkspaceRole.Assistant);
    }

    return checkAll([Plan.Admin, Plan.Annotation, Plan.Training, Plan.Inactive], permittedRolesScope, currentWorkspace, t);
  };

  const canDeleteMarkedImages = (): IPermissionCheckResult => {
    return checkAll([Plan.Admin, Plan.Annotation, Plan.Training, Plan.Inactive], [WorkspaceRole.Owner, WorkspaceRole.Manager], currentWorkspace, t);
  };

  const canDownloadImages = (datasetStatus: DatasetStatus, datasetCreatorId: string): IPermissionCheckResult => {
    const permittedRolesScope = [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer, WorkspaceRole.Collaborator];

    if (datasetStatus !== 'Published' && datasetStatus !== 'PublishedUploading' && userInfo.id === datasetCreatorId) {
      permittedRolesScope.push(WorkspaceRole.Assistant);
    }

    return checkAll([Plan.Admin, Plan.Annotation, Plan.Training, Plan.Free, Plan.Inactive], permittedRolesScope, currentWorkspace, t);
  };

  const canCreateDataset = (): IPermissionCheckResult => {
    const permittedRolesScope = [
      WorkspaceRole.Owner,
      WorkspaceRole.Manager,
      WorkspaceRole.Developer,
      WorkspaceRole.Trainer,
      WorkspaceRole.Collaborator,
      WorkspaceRole.Assistant,
    ];

    return checkAll([Plan.Admin, Plan.Annotation, Plan.Training], permittedRolesScope, currentWorkspace, t);
  };

  const canUploadImages = (datasetStatus: DatasetStatus, datasetCreatorId: string): IPermissionCheckResult => {
    const permittedRolesScope =
      datasetStatus === 'Draft'
        ? [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer, WorkspaceRole.Collaborator]
        : [WorkspaceRole.Owner, WorkspaceRole.Manager];

    if ((datasetStatus !== 'Published' && datasetStatus !== 'PublishedUploading' && userInfo.id === datasetCreatorId) || datasetCreatorId === '') {
      permittedRolesScope.push(WorkspaceRole.Assistant);
    }

    return checkAll([Plan.Admin, Plan.Annotation, Plan.Training], permittedRolesScope, currentWorkspace, t);
  };

  const canSeeDatasetCreatedBy = (): IPermissionCheckResult => {
    return checkWorkspaceRole([WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer], currentWorkspace, t);
  };

  const canEditDatasetDetails = (datasetStatus: DatasetStatus, datasetCreatorId: string): IPermissionCheckResult => {
    const permittedRolesScope = [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer, WorkspaceRole.Collaborator];

    if (datasetStatus !== 'Published' && datasetStatus !== 'PublishedUploading' && userInfo.id === datasetCreatorId) {
      permittedRolesScope.push(WorkspaceRole.Assistant);
    }

    return checkAll([Plan.Admin, Plan.Annotation, Plan.Training], permittedRolesScope, currentWorkspace, t);
  };

  const canPublishDataset = (): IPermissionCheckResult => {
    const permittedRolesScope = [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer, WorkspaceRole.Collaborator];

    return checkAll([Plan.Admin, Plan.Annotation, Plan.Training], permittedRolesScope, currentWorkspace, t);
  };

  const canDownloadDataset = (): IPermissionCheckResult => {
    const permittedRolesScope = [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer];

    return checkAll([Plan.Admin, Plan.Annotation, Plan.Training, Plan.Free, Plan.Inactive], permittedRolesScope, currentWorkspace, t);
  };

  const canDeleteDataset = (datasetStatus: DatasetStatus, datasetCreatorId: string): IPermissionCheckResult => {
    const permittedRolesScope = [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Developer, WorkspaceRole.Trainer, WorkspaceRole.Collaborator];

    if (datasetStatus !== 'Published' && datasetStatus !== 'PublishedUploading' && userInfo.id === datasetCreatorId) {
      permittedRolesScope.push(WorkspaceRole.Assistant);
    }

    return checkWorkspaceRole(permittedRolesScope, currentWorkspace, t);
  };

  return {
    canViewDatasets,
    canRenameImages,
    canDeleteUnmarkedImages,
    canDeleteMarkedImages,
    canDownloadImages,
    canCreateDataset,
    canUploadImages,
    canEditDatasetDetails,
    canSeeDatasetCreatedBy,
    canPublishDataset,
    canDownloadDataset,
    canDeleteDataset,
  };
};
